import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { stripeCheckout } from '@/redux/app/actions/organization';
import { NavigationPaneRouteParamsContext } from '@/lib/navigation';
import { orgHasStripeSubscriptionSelector } from '@/redux/app/selectors/organization';
import { ColumnFeatures } from './ColumnFeatures';
import { PlanSection } from './PlanSection';
import { v4PlanPrices } from '../../../constants/pricing';
import { CustomPlanColumnSubtitle } from './CustomPlanColumnSubtitle';
import { TrialPlanColumnSubtitle } from './TrialPlanColumnSubtitle';
import { StandardPlanColumnSubtitle } from './StandardPlanColumnSubtitle';
import { ContactUsButton } from './ContactUsButton';

interface ColumnProps {
  seatCount: number;
  setSeatCount: (seatCount: number) => void;
  columnData: Record<string, any>;
  togglePayPeriod: string;
  selectedPlan: string;
  handleShowModal: () => void;
}
export const Column = ({
  seatCount, setSeatCount, columnData, togglePayPeriod,
  selectedPlan, handleShowModal,
}: ColumnProps) => {
  const { subdomain } = React.useContext(NavigationPaneRouteParamsContext);
  const dispatch = useDispatch();
  const hasStripeSubscription = useSelector(orgHasStripeSubscriptionSelector);
  const { id, name, nameColor, featureSections } = columnData;
  const [gaClientId, setGaClientId] = React.useState('');

  const fetchGaClientId = () => {
    if (!window.ga || !window.ga.loaded) return setTimeout(fetchGaClientId, 500);

    const item = window.ga.getAll()[0];
    if (!item) return null;
    return setGaClientId(item.get('clientId'));
  };

  React.useEffect(() => { fetchGaClientId(); }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(stripeCheckout({
      planId: `${id}-${togglePayPeriod}`,
      subdomain,
      seatCount,
      gaClientId,
    }));
  };

  const handleSeatChange = (count) => setSeatCount(count);
  const isCustomPlan = id === 'custom';
  const isStandardPlan = id === 'standard-plan-4';
  const isTrialPlan = id === 'trial-plan-4';

  const emptyPlaceholder = <div style={{ minHeight: '23px' }} />;
  const buttonClassName = 'button align-self-c lg mt-10 bright-green width-100';
  const containerClassName = `plan-container ${id === selectedPlan ? 'selected' : ''} ${isStandardPlan ? 'pop' : ''}`;

  let bigPrice;
  let selectButton;
  let perMonth;
  let perUser;
  let subtitle;

  if (!isCustomPlan) {
    const key = isTrialPlan ? id : `${id}-${togglePayPeriod}`;
    const planPrices = v4PlanPrices[key];

    bigPrice = (
      <span className="black fsz-30 bold-800">
        $
        {planPrices.seatPrice}
      </span>
    );
    perMonth = <span className="fsz-16 bold-800">/mo</span>;
    perUser = <span className="fsz-16">&nbsp;per user</span>;

    selectButton = (
      <button type="button" className={buttonClassName} onClick={hasStripeSubscription ? handleShowModal : handleSubmit}>
        Secure Checkout
      </button>
    );

    if (isTrialPlan) {
      subtitle = <TrialPlanColumnSubtitle />;
    } else if (isStandardPlan) {
      subtitle = <StandardPlanColumnSubtitle togglePayPeriod={togglePayPeriod} />;
    }
  } else {
    subtitle = <CustomPlanColumnSubtitle />;
    bigPrice = <span className="fsz-30 bold-800">Custom</span>;
    selectButton = <ContactUsButton />;
  }

  const popularHeader = !isStandardPlan ? emptyPlaceholder : <div className="popular">MOST POPULAR</div>;
  const planNameClass = `${nameColor} center fsz-20 uppercase letter-space-3 mt-15`;

  return (
    <div className="column">
      <div className={containerClassName}>
        <div className="container-top">
          <div className="row-1">
            {popularHeader}
            <h2 className={planNameClass}>{name}</h2>
            <div className="subscription-fees flex-col my-25">
              <div className="align-self-c">
                {bigPrice}
                {perMonth}
                {perUser}
              </div>
              {subtitle}
            </div>
            <PlanSection
              planId={id}
              handleSeatChange={handleSeatChange}
              seatCount={seatCount}
              togglePayPeriod={togglePayPeriod}
              planName={name}
            />
          </div>
          {selectButton}
        </div>
      </div>
      <div>
        <ColumnFeatures featureSections={featureSections} />
        <br />
        <p>And more</p>
      </div>
    </div>
  );
};
